import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const Integrations = () => {
  const [servers, setServers] = useState([]);
  const [serversWithTools, setServersWithTools] = useState({});
  const [expandedServers, setExpandedServers] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServers();
  }, []);

  const fetchServers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/integrations/servers`);
      setServers(response.data.data);
      
      // Fetch tools for each server
      const toolsData = {};
      for (const server of response.data.data) {
        try {
          const toolsResponse = await axios.get(`${API_BASE_URL}/api/admin/integrations/servers/${server.id}`);
          toolsData[server.id] = toolsResponse.data.data.tools || [];
        } catch (err) {
          console.error(`Error fetching tools for ${server.id}:`, err);
          toolsData[server.id] = [];
        }
      }
      setServersWithTools(toolsData);
    } catch (error) {
      console.error('Error fetching integration servers:', error);
      toast.error('Failed to fetch integration servers');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (serverId) => {
    setExpandedServers(prev => ({
      ...prev,
      [serverId]: !prev[serverId]
    }));
  };

  const toggleServerEnabled = async (serverId, currentStatus) => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/integrations/servers/${serverId}/toggle-enabled`);
      toast.success(`Integration ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
      fetchServers();
    } catch (error) {
      console.error('Error toggling integration:', error);
      toast.error('Failed to toggle integration');
    }
  };

  const toggleToolEnabled = async (toolId, currentStatus) => {
    try {
      await axios.put(`${API_BASE_URL}/api/admin/tools/${toolId}`, {
        is_enabled: !currentStatus
      });
      toast.success(`Tool ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
      fetchServers();
    } catch (error) {
      console.error('Error toggling tool:', error);
      toast.error('Failed to toggle tool');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      email: 'bg-blue-100 text-blue-800',
      analytics: 'bg-purple-100 text-purple-800',
      payment: 'bg-green-100 text-green-800',
      seo: 'bg-yellow-100 text-yellow-800',
      social: 'bg-pink-100 text-pink-800',
      accessibility: 'bg-indigo-100 text-indigo-800',
      image: 'bg-orange-100 text-orange-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="py-12">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-8">System Integrations</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {servers.map((server) => (
            <div 
              key={server.id}
              className={`bg-white rounded-lg shadow-md p-6 border-2 transition-all ${
                server.is_enabled 
                  ? 'border-green-300 hover:shadow-lg' 
                  : 'border-gray-200 opacity-60 hover:opacity-80'
              }`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{server.name}</h3>
                  <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${getCategoryColor(server.category)}`}>
                    {server.category}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {server.is_enabled ? (
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                      ✓ Enabled
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-gray-200 text-gray-600 text-xs font-bold rounded-full">
                      Disabled
                    </span>
                  )}
                </div>
              </div>

              {/* Description */}
              {server.description && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {server.description}
                </p>
              )}

              {/* System badge */}
              {server.is_system && (
                <div className="mb-4">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                    🔒 System Integration
                  </span>
                </div>
              )}

              {/* Details */}
              <div className="space-y-2 mb-4 text-xs text-gray-600">
                <div>
                  <span className="font-semibold">ID:</span> <span className="font-mono">{server.id}</span>
                </div>
                {server.command && (
                  <div>
                    <span className="font-semibold">Command:</span> <span className="font-mono text-xs">{server.command}</span>
                  </div>
                )}
                <div>
                  <span className="font-semibold">Created:</span> {new Date(server.created_at).toLocaleDateString()}
                </div>
              </div>

              {/* Tools Section */}
              {serversWithTools[server.id] && serversWithTools[server.id].length > 0 && (
                <div className="mb-4">
                  <button
                    onClick={() => toggleExpanded(server.id)}
                    className="w-full flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <span className="text-sm font-semibold text-gray-700">
                      Tools ({serversWithTools[server.id].length})
                    </span>
                    <span className="text-gray-500">
                      {expandedServers[server.id] ? '▼' : '▶'}
                    </span>
                  </button>
                  
                  {expandedServers[server.id] && (
                    <div className="mt-2 space-y-2 pl-3">
                      {serversWithTools[server.id].map((tool) => (
                        <div 
                          key={tool.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-200"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-gray-800">{tool.name}</p>
                            {tool.description && (
                              <p className="text-xs text-gray-600 mt-1 line-clamp-1">{tool.description}</p>
                            )}
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer ml-3">
                            <input
                              type="checkbox"
                              checked={tool.is_enabled}
                              onChange={() => toggleToolEnabled(tool.id, tool.is_enabled)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t border-gray-200">
                <button
                  onClick={() => toggleServerEnabled(server.id, server.is_enabled)}
                  className={`flex-1 py-2 rounded-lg font-semibold text-sm transition-colors ${
                    server.is_enabled
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {server.is_enabled ? 'Disable Integration' : 'Enable Integration'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {servers.length === 0 && (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-500">No integration servers found</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Integrations;

